import { useMemo } from 'react';
import { useData } from '../../../utils/hooks/useData';
import { formatFromAndToQuery, calculatePercentageDifference, calculatePercetageOfSecondValue } from '../../lib';
import { AnalyticsStoreProvider, useAnalyticsStoreContext } from '../../contexts';

import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import StatCard from '../../../card/stat-card/StatCard';
import AnalyticsError from '../../AnalyticsError';
import DateRangeComparatorInput from '../../inputs/DateRangeComparatorInput';
import InfoDetails from '../../../bootstrap/InfoDetails';
import Row from './_components/Row';

const ROW_HEIGHT = 40;
const PARENT_ROW_HEIGHT = 350;

const gaMetricsForPath = (gaMetricsForPages, path, source, medium) => {
  const items = gaMetricsForPages.filter((item) => item.page_path === path && item.source === source);

  if (items.length === 0) return -1;

  if (source === '(direct)') return parseInt(items[0].view_count, 10);

  const item = items.find((item) => item.medium === medium);

  if (!item) return -1;

  return parseInt(item.view_count, 10);
};

function Submissions({ endpoints }) {
  const {
    form_submission_sources_api_v1_cms_analytic_path,
    ga_metrics_for_pages_api_v1_cms_analytic_path,
    total_submissions_api_v1_cms_analytic_path,
    submissions_during_period_api_v1_cms_analytic_path,
  } = endpoints;

  const memoizedGaMetricsForPath = useMemo(() => {
    return (gaMetricsForPages, path, source, medium) => gaMetricsForPath(gaMetricsForPages, path, source, medium);
  }, []);

  const dateRange = useAnalyticsStoreContext((state) => state.data.first.dateRange);
  const dateRangeTwo = useAnalyticsStoreContext((state) => state.data.second.dateRange);
  const isComparing = useAnalyticsStoreContext((state) => state.isComparing);

  const { data: totalSubmissions } = useData(`${total_submissions_api_v1_cms_analytic_path}.json`);

  const { data: submissionsDuringPeriod } = useData(`${submissions_during_period_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRange)}`);

  const { data: submissionsDuringPeriodTwo } = useData(
    `${submissions_during_period_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
  );

  const {
    data: formSubmission,
    isLoading: isFormSubmissionLoading,
    error: formSubmissionError,
  } = useData(`${form_submission_sources_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRange)}`);

  const {
    data: formSubmissionTwo,
    isLoading: isFormSubmissionTwoLoading,
    error: formSubmissionTwoError,
  } = useData(
    `${form_submission_sources_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
  );

  const {
    data: gaMetricsForPages,
    isLoading: isGaMetricsForPagesLoading,
    error: gaMetricsForPagesError,
  } = useData(`${ga_metrics_for_pages_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRange)}`);

  const {
    data: gaMetricsForPagesTwo,
    isLoading: isGaMetricsForPagesTwoLoading,
    error: gaMetricsForPagesTwoError,
  } = useData(
    `${ga_metrics_for_pages_api_v1_cms_analytic_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
  );

  const mergedFormSubmissions = useMemo(() => {
    if (!formSubmission) return {};

    const merged = { ...formSubmission };

    if (formSubmissionTwo) {
      Object.entries(formSubmissionTwo).forEach(([path, sources]) => {
        if (!merged[path]) {
          merged[path] = sources.map((source) => ({ ...source, countTwo: source.count }));
        } else {
          sources.forEach((sourceTwo) => {
            const existingSource = merged[path].find((source) => source.source === sourceTwo.source && source.medium === sourceTwo.medium);
            if (existingSource) {
              existingSource.countTwo = sourceTwo.count;
            } else {
              merged[path].push({ ...sourceTwo, countTwo: sourceTwo.count });
            }
          });
        }
      });
    }

    return merged;
  }, [formSubmission, formSubmissionTwo]);

  const parentEntries = useMemo(() => Object.entries(mergedFormSubmissions), [mergedFormSubmissions]);

  return (
    <div id="form-insights" className="d-flex flex-column gap-4" style={{ maxWidth: 1300 }}>
      <div className="row">
        <div className="col" />
        <div className="col-auto" id="date-range">
          <DateRangeComparatorInput />
        </div>
      </div>

      <div className="row">
        <StatCard title="Total Submissions (All Time)" value={totalSubmissions ?? '-'} iconClass="fe fe-user-check" />
        <StatCard
          title="Total Submissions (Selected Period)"
          value={submissionsDuringPeriod ?? '-'}
          comparingValue={
            isComparing ? `${submissionsDuringPeriodTwo} (${calculatePercentageDifference(submissionsDuringPeriod, submissionsDuringPeriodTwo)})` : null
          }
          iconClass="fe fe-user-check"
        />
      </div>

      {isFormSubmissionLoading || isFormSubmissionTwoLoading || isGaMetricsForPagesLoading || isGaMetricsForPagesTwoLoading ? (
        <div className="card d-flex align-items-center justify-content-center" style={{ height: 500 }}>
          <div className="spinner-border" role="loading">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : formSubmissionError || formSubmissionTwoError || gaMetricsForPagesError || gaMetricsForPagesTwoError ? (
        <div className="card d-flex flex-column align-items-center justify-content-center" style={{ height: 500 }}>
          {formSubmissionError && (
            <div className="d-flex gap-2">
              <span>Form submissions:</span>
              <AnalyticsError errorMsg={formSubmissionError} />
            </div>
          )}
          {formSubmissionTwoError && (
            <div className="d-flex gap-2">
              <span>Second form submissions:</span>
              <AnalyticsError errorMsg={formSubmissionTwoError} />
            </div>
          )}
          {gaMetricsForPagesError && (
            <div className="d-flex gap-2">
              <span>GA Metrics for pages:</span>
              <AnalyticsError errorMsg={gaMetricsForPagesError} />
            </div>
          )}
          {gaMetricsForPagesTwoError && (
            <div className="d-flex gap-2">
              <span>Second GA Metrics for pages:</span>
              <AnalyticsError errorMsg={gaMetricsForPagesTwoError} />
            </div>
          )}
        </div>
      ) : parentEntries.length ? (
        <div style={{ height: 600 }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                itemCount={parentEntries.length}
                itemSize={PARENT_ROW_HEIGHT}
                width={width}
                itemData={{
                  parentEntries,
                  isComparing,
                  memoizedGaMetricsForPath,
                  gaMetricsForPages,
                  gaMetricsForPagesTwo,
                  calculatePercentageDifference,
                  calculatePercetageOfSecondValue,
                }}
              >
                {({ index, style, data }) => {
                  const {
                    parentEntries,
                    isComparing,
                    memoizedGaMetricsForPath,
                    gaMetricsForPages,
                    gaMetricsForPagesTwo,
                    calculatePercentageDifference,
                    calculatePercetageOfSecondValue,
                  } = data;

                  const [path, sources] = parentEntries[index];

                  return (
                    <div key={path} style={style}>
                      <div className="card mb-5 me-3">
                        <div className="card-header text-muted">{path}</div>
                        <div>
                          {/* Header */}
                          <div
                            className="d-flex align-items-center"
                            style={{
                              borderBottom: '1px solid #312852',
                              backgroundColor: '#221A3B',
                              fontWeight: 'bold',
                              fontSize: 11,
                            }}
                          >
                            <div className="text-muted" style={{ width: '25%', padding: '0.5rem', textTransform: 'uppercase' }}>
                              Source
                            </div>
                            <div className="text-muted" style={{ width: '25%', padding: '0.5rem', textTransform: 'uppercase' }}>
                              Conversions
                            </div>
                            <div className="text-muted" style={{ width: '25%', padding: '0.5rem', textTransform: 'uppercase' }}>
                              <div className="d-flex gap-2 align-items-center">
                                <span>Page Views</span>
                                <InfoDetails text={'These numbers are pulled from Google Analytics if any data can be found for the same source.'} />
                              </div>
                            </div>
                            <div className="text-muted" style={{ width: '25%', padding: '0.5rem', textTransform: 'uppercase' }}>
                              Conversion Rate
                            </div>
                          </div>

                          {/* Virtualized Rows for Sources */}
                          <div style={{ height: 210 }}>
                            <AutoSizer>
                              {({ height, width }) => (
                                <List
                                  height={height}
                                  itemCount={sources.length}
                                  itemSize={ROW_HEIGHT}
                                  width={width}
                                  itemData={{
                                    sources,
                                    path,
                                    isComparing,
                                    memoizedGaMetricsForPath,
                                    gaMetricsForPages,
                                    gaMetricsForPagesTwo,
                                    calculatePercentageDifference,
                                    calculatePercetageOfSecondValue,
                                  }}
                                >
                                  {({ index, style }) => (
                                    <Row
                                      style={style}
                                      data={{
                                        source: sources[index],
                                        path,
                                        isComparing,
                                        memoizedGaMetricsForPath,
                                        gaMetricsForPages,
                                        gaMetricsForPagesTwo,
                                        calculatePercentageDifference,
                                        calculatePercetageOfSecondValue,
                                      }}
                                    />
                                  )}
                                </List>
                              )}
                            </AutoSizer>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }}
              </List>
            )}
          </AutoSizer>
        </div>
      ) : (
        <div className="card d-flex align-items-center justify-content-center" style={{ height: 500 }}>
          <span className="fs-3 text-muted">There's no analytics data available for this form. 🙇‍♂️</span>
        </div>
      )}
    </div>
  );
}

export default function FormSubmission(props) {
  return (
    <AnalyticsStoreProvider>
      <Submissions {...props} />
    </AnalyticsStoreProvider>
  );
}
